import React, {useMemo, useState, useEffect, useCallback} from 'react';
import {Layer, Marker, Source} from 'react-map-gl';
import type {LayerProps} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {MapPin, Flag, Store as StoreIcon, Pause, Play} from 'lucide-react';
import type {SessionTrack, SessionTrackPoint} from '../models/session.model';
import {MapView} from '../../../Presentation/components/common/MapView';

interface MarketerPathMapProps {
  track: SessionTrack | null;
  loading?: boolean;
  height?: number | string;
  selectedVisitId?: number | null;
  onVisitClick?: (visitId: number) => void;
}

// Riyadh fallback viewport when there's nothing to show yet.
const FALLBACK_VIEW = {
  latitude: 24.7136,
  longitude: 46.6753,
  zoom: 11
};

const PATH_LAYER: LayerProps = {
  id: 'path-layer',
  type: 'line',
  layout: {
    'line-cap': 'round',
    'line-join': 'round'
  },
  paint: {
    'line-color': '#2563eb',
    'line-width': 4,
    'line-opacity': 0.85
  }
};

type LatLng = {lat: number; lng: number};

function flattenSegments (segments: Array<{points: LatLng[]}>): LatLng[] {
  const out: LatLng[] = [];
  for (const seg of segments) out.push(...seg.points);
  return out;
}

function computeViewState (points: LatLng[]) {
  if (points.length === 0) return FALLBACK_VIEW;
  if (points.length === 1) {
    return {latitude: points[0].lat, longitude: points[0].lng, zoom: 15};
  }
  let minLat = points[0].lat;
  let maxLat = points[0].lat;
  let minLng = points[0].lng;
  let maxLng = points[0].lng;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;
  const latSpan = maxLat - minLat;
  const lngSpan = maxLng - minLng;
  const span = Math.max(latSpan, lngSpan);
  let zoom = 16;
  if (span > 0.5) zoom = 9;
  else if (span > 0.2) zoom = 10;
  else if (span > 0.1) zoom = 11;
  else if (span > 0.05) zoom = 12;
  else if (span > 0.02) zoom = 13;
  else if (span > 0.01) zoom = 14;
  else if (span > 0.005) zoom = 15;
  else if (span > 0.001) zoom = 16;
  else if (span > 0.0005) zoom = 17;
  else zoom = 18; // sub-50m paths — zoom all the way in
  return {latitude: centerLat, longitude: centerLng, zoom};
}

/**
 * Renders the marketer's road-snapped path on top of the project's shared
 * `MapView` wrapper. `MapView` already implements the full tile-source
 * cascade — Mapbox primary → Mapbox fallback → OpenStreetMap (offline) →
 * complete-offline placeholder — so when the Mapbox token fails (revoked,
 * URL-restricted, monthly quota hit, network blocked) the path keeps
 * rendering on OSM tiles automatically. No bespoke retry logic here.
 */
export const MarketerPathMap: React.FC<MarketerPathMapProps> = ({
  track,
  loading = false,
  height = 520,
  selectedVisitId = null,
  onVisitClick
}) => {
  const segments = track?.segments ?? [];
  const markers = track?.markers ?? [];

  const flatPoints = useMemo(() => flattenSegments(segments), [segments]);

  // MapView is a controlled component — it expects viewState + onMove callbacks.
  // We seed the viewport from the path's bounding box on every track change so
  // the map auto-centres when the user picks a different session.
  const [viewState, setViewState] = useState(() => computeViewState(flatPoints));

  useEffect(() => {
    setViewState(computeViewState(flatPoints));
    // recenter only when a different session/path is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track?.session.id, segments.length]);

  const handleMove = useCallback((e: any) => {
    const v = e.viewState;
    setViewState({latitude: v.latitude, longitude: v.longitude, zoom: v.zoom});
  }, []);
  const noop = useCallback(() => undefined, []);

  const geojson = useMemo<GeoJSON.Feature<GeoJSON.MultiLineString> | null>(() => {
    const validSegs = segments.filter((s) => s.points.length >= 2);
    if (validSegs.length === 0) return null;
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'MultiLineString',
        coordinates: validSegs.map((s) => s.points.map((p) => [p.lng, p.lat]))
      }
    };
  }, [segments]);

  const dayStart = useMemo<SessionTrackPoint | null>(() => {
    return markers.find((m) => m.trackType === 'clock_in') ?? null;
  }, [markers]);
  const dayEnd = useMemo<SessionTrackPoint | null>(() => {
    const clockOuts = markers.filter((m) => m.trackType === 'clock_out');
    return clockOuts.length > 0 ? clockOuts[clockOuts.length - 1] : null;
  }, [markers]);

  const pauseMarkers = useMemo(
    () => markers.filter((m) => m.trackType === 'pause'),
    [markers]
  );
  const resumeMarkers = useMemo(
    () => markers.filter((m) => m.trackType === 'resume'),
    [markers]
  );
  const visitStartMarkers = useMemo(
    () => markers.filter((m) => m.trackType === 'visit_start'),
    [markers]
  );

  return (
    <div className="relative rounded-lg overflow-hidden border border-gray-200" style={{height}}>
      <MapView
        viewState={viewState}
        onMoveStart={noop}
        onMove={handleMove}
        onMoveEnd={noop}
      >
        {geojson && (
          <Source id="path-src" type="geojson" data={geojson}>
            <Layer {...PATH_LAYER} />
          </Source>
        )}

        {dayStart && (
          <Marker longitude={dayStart.lng} latitude={dayStart.lat} anchor="bottom">
            <div className="flex flex-col items-center" title="بداية اليوم">
              <div className="rounded-full bg-green-600 p-2 text-white shadow-lg">
                <Flag className="h-4 w-4" />
              </div>
            </div>
          </Marker>
        )}

        {dayEnd && (
          <Marker longitude={dayEnd.lng} latitude={dayEnd.lat} anchor="bottom">
            <div className="flex flex-col items-center" title="نهاية اليوم">
              <div className="rounded-full bg-red-600 p-2 text-white shadow-lg">
                <Flag className="h-4 w-4" />
              </div>
            </div>
          </Marker>
        )}

        {pauseMarkers.map((p, idx) => (
          <Marker
            key={`pause-${idx}-${p.recordedAt.toISOString()}`}
            longitude={p.lng}
            latitude={p.lat}
            anchor="bottom"
          >
            <div
              className="rounded-full bg-gray-500 p-1.5 text-white shadow-md"
              title={`استراحة ${p.recordedAt.toLocaleTimeString()}`}
            >
              <Pause className="h-3 w-3" />
            </div>
          </Marker>
        ))}

        {resumeMarkers.map((p, idx) => (
          <Marker
            key={`resume-${idx}-${p.recordedAt.toISOString()}`}
            longitude={p.lng}
            latitude={p.lat}
            anchor="bottom"
          >
            <div
              className="rounded-full bg-amber-500 p-1.5 text-white shadow-md"
              title={`استئناف ${p.recordedAt.toLocaleTimeString()}`}
            >
              <Play className="h-3 w-3" />
            </div>
          </Marker>
        ))}

        {visitStartMarkers.map((p, idx) => {
          const isSelected = p.visitId !== null && p.visitId === selectedVisitId;
          return (
            <Marker
              key={`visit-${p.visitId ?? idx}-${p.recordedAt.toISOString()}`}
              longitude={p.lng}
              latitude={p.lat}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent?.stopPropagation?.();
                if (p.visitId !== null && onVisitClick) {
                  onVisitClick(p.visitId);
                }
              }}
            >
              <div
                title={p.storeName ?? 'متجر'}
                className={`cursor-pointer rounded-full p-1.5 text-white shadow-md transition-transform ${
                  isSelected
                    ? 'scale-125 bg-blue-700 ring-2 ring-blue-300'
                    : 'bg-blue-600 hover:scale-110'
                }`}
              >
                <StoreIcon className="h-3.5 w-3.5" />
              </div>
            </Marker>
          );
        })}
      </MapView>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-30">
          <div className="flex items-center gap-2 rounded-md bg-white px-4 py-2 shadow">
            <MapPin className="h-4 w-4 animate-pulse" />
            <span>جارٍ تحميل المسار...</span>
          </div>
        </div>
      )}

      {!loading && !track && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/90 text-gray-600 z-30">
          اختر مسوّقًا وتاريخًا لعرض المسار
        </div>
      )}

      {!loading && track && segments.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/90 text-gray-600 z-30">
          لا توجد نقاط مسجّلة في هذه الجلسة
        </div>
      )}
    </div>
  );
};
